import { apiFetch } from "../../lib/api";
import { useState, useEffect, useCallback, useRef } from "react";
import { useWindowManager } from "../../context/WindowManagerContext";
import type { SnapZone } from "../../context/WindowManagerContext";
import { readBootstrap, subscribeBootstrap, type UiBootstrap } from "../../lib/bootstrap";
import { initWebMode } from "../../lib/web-ipc";
import { APP_REGISTRY, GROUP_ORDER, PINNED_APPS } from "../../lib/appRegistry";
import { trackRecentApp } from "./StartMenu";
import { DesktopBackground } from "./DesktopBackground";
import { DesktopIconGrid } from "./DesktopIconGrid";
import { Window } from "./Window";
import { Taskbar } from "./Taskbar";
import { WindowSwitcher } from "./WindowSwitcher";
import { useContextMenu, type ContextMenuItem } from "./ContextMenu";
import { SpotlightLauncher } from "./SpotlightLauncher";
import { KeyboardShortcutsOverlay } from "../ui/KeyboardShortcutsOverlay";
import { ToastNotifications } from "../ui/ToastNotifications";

const SNAP_PREVIEW_PAD = 6;
const TASKBAR_H = 48;

function getSnapPreviewRect(zone: SnapZone): React.CSSProperties | null {
  if (!zone) return null;
  const w = window.innerWidth;
  const h = window.innerHeight - TASKBAR_H;
  const halfW = Math.floor(w / 2);
  const halfH = Math.floor(h / 2);
  const p = SNAP_PREVIEW_PAD;

  switch (zone) {
    case "top":
      return { left: p, top: p, width: w - p * 2, height: h - p * 2 };
    case "left":
      return { left: p, top: p, width: halfW - p * 2, height: h - p * 2 };
    case "right":
      return { left: halfW + p, top: p, width: w - halfW - p * 2, height: h - p * 2 };
    case "top-left":
      return { left: p, top: p, width: halfW - p * 2, height: halfH - p * 2 };
    case "top-right":
      return { left: halfW + p, top: p, width: w - halfW - p * 2, height: halfH - p * 2 };
    case "bottom-left":
      return { left: p, top: halfH + p, width: halfW - p * 2, height: h - halfH - p * 2 };
    case "bottom-right":
      return { left: halfW + p, top: halfH + p, width: w - halfW - p * 2, height: h - halfH - p * 2 };
    default:
      return null;
  }
}

function SnapPreview({ zone }: { zone: SnapZone }) {
  const rect = getSnapPreviewRect(zone);
  if (!rect) return null;
  return <div className="wm-snap-preview" style={rect} />;
}

interface DesktopProps {
  children: (route: string, payload: unknown) => React.ReactNode;
}

export function Desktop({ children }: DesktopProps) {
  const {
    windows,
    activeId,
    initialized,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    focusWindow,
    moveWindow,
    resizeWindow,
    snapWindow,
    unsnapWindow,
    minimizeAll,
    restoreAll,
    closeAllWindows,
    cascadeWindows,
    tileHorizontally,
    tileVertically,
    setAlwaysOnTop,
    cycleWindow,
    applyPreset,
  } = useWindowManager();

  const desktopCtx = useContextMenu();

  const [provider, setProvider] = useState("detecting...");
  const [switcherVisible, setSwitcherVisible] = useState(false);
  const [switcherHighlight, setSwitcherHighlight] = useState<string | null>(null);
  const switcherTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const desktopMinimizedRef = useRef(false);
  const [activeSnapZone, setActiveSnapZone] = useState<SnapZone>(null);
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const recentWindowsRef = useRef<string[]>([]);

  // Handle drag cursor move from a window - detect snap zone
  const handleDragCursorMove = useCallback((windowId: string) => {
    return (pos: { x: number; y: number } | null) => {
      if (!pos) {
        setActiveSnapZone(null);
        return;
      }
      const SNAP_EDGE_PX = 8;
      const TASKBAR_HEIGHT = 48;
      const w = window.innerWidth;
      const h = window.innerHeight - TASKBAR_HEIGHT;
      const { x, y } = pos;

      const atLeft = x <= SNAP_EDGE_PX;
      const atRight = x >= w - SNAP_EDGE_PX;
      const atTop = y <= SNAP_EDGE_PX;
      const atBottom = y >= h - SNAP_EDGE_PX;

      let zone: SnapZone = null;
      if (atTop && atLeft) zone = "top-left";
      else if (atTop && atRight) zone = "top-right";
      else if (atBottom && atLeft) zone = "bottom-left";
      else if (atBottom && atRight) zone = "bottom-right";
      else if (atTop) zone = "top";
      else if (atLeft) zone = "left";
      else if (atRight) zone = "right";

      setActiveSnapZone(zone);
    };
  }, []);

  // Initialize web mode
  useEffect(() => {
    initWebMode().then((isWeb) => {
      if (isWeb) {
        apiFetch("/api/status")
          .then((r) => r.json())
          .then((d) => setProvider(d.active_provider || "unknown"))
          .catch(() => {});
      }
    });
  }, []);

  // Listen for SSE provider changes
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.provider) setProvider(detail.provider);
    };
    window.addEventListener("opentool-ipc", handler);
    return () => window.removeEventListener("opentool-ipc", handler);
  }, []);

  // Hash-based routing → open/focus windows
  const handleRouteChange = useCallback(
    (route: string) => {
      if (route) {
        openWindow(route);
        trackRecentApp(route);
        // Track recent windows for Spotlight
        recentWindowsRef.current = [
          route,
          ...recentWindowsRef.current.filter((r) => r !== route),
        ].slice(0, 10);
        if (window.location.hash.slice(1) !== route) {
          window.location.hash = route;
        }
      }
    },
    [openWindow],
  );

  // Listen for backend-driven navigation
  useEffect(() => {
    return subscribeBootstrap((b: UiBootstrap) => {
      if (b.route) handleRouteChange(b.route);
    });
  }, [handleRouteChange]);

  // Listen for agent-driven app opens (e.g. navigate_browser tool)
  useEffect(() => {
    const onOpenApp = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.id) handleRouteChange(detail.id);
    };
    window.addEventListener("opentool-open-app", onOpenApp);
    return () => window.removeEventListener("opentool-open-app", onOpenApp);
  }, [handleRouteChange]);

  // Apply dashboard preset once context is initialized and desktop is empty
  const hasAppliedPreset = useRef(false);
  useEffect(() => {
    if (!initialized || hasAppliedPreset.current) return;
    hasAppliedPreset.current = true;

    const hash = window.location.hash.slice(1);
    if (hash) {
      handleRouteChange(hash);
    } else if (windows.length === 0) {
      // No windows restored from localStorage — show the default layout
      applyPreset("dashboard");
    }
  }, [initialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Support URL hash routing
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.slice(1);
      if (h) handleRouteChange(h);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [handleRouteChange]);

  // Derive current active route for children render
  const activeRoute = activeId || "home";

  // ── OS-level keyboard shortcuts ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const alt = e.altKey;

      // Alt+Tab — cycle windows with switcher overlay
      if (alt && key === "tab") {
        e.preventDefault();
        const nextId = cycleWindow(e.shiftKey ? "prev" : "next");
        if (nextId) {
          setSwitcherHighlight(nextId);
          setSwitcherVisible(true);
          if (switcherTimerRef.current) clearTimeout(switcherTimerRef.current);
          switcherTimerRef.current = setTimeout(() => {
            setSwitcherVisible(false);
            setSwitcherHighlight(null);
          }, 1200);
        }
        return;
      }

      // Alt+F4 or Ctrl+W — close active window
      if ((alt && key === "f4") || (ctrl && key === "w")) {
        e.preventDefault();
        if (activeId) closeWindow(activeId);
        return;
      }

      // Alt+Enter or F11 — toggle maximize
      if ((alt && key === "enter") || key === "f11") {
        e.preventDefault();
        if (activeId) {
          const win = windows.find((w) => w.id === activeId);
          if (win) {
            if (win.status === "maximized") restoreWindow(activeId);
            else maximizeWindow(activeId);
          }
        }
        return;
      }

      // Alt+M — minimize active window
      if (alt && key === "m") {
        e.preventDefault();
        if (activeId) minimizeWindow(activeId);
        return;
      }

      // Ctrl+` — open/focus Chat window
      if (ctrl && (key === "`" || key === "dead")) {
        e.preventDefault();
        openWindow("chat");
        return;
      }

      // Ctrl+Space or Ctrl+K — toggle Spotlight launcher
      if ((ctrl && key === " ") || (ctrl && key === "k")) {
        e.preventDefault();
        setSpotlightOpen((v) => !v);
        return;
      }

      // Meta key — toggle Start Menu
      if (key === "meta") {
        return;
      }

      // Ctrl+D — show desktop / restore
      if (ctrl && key === "d") {
        e.preventDefault();
        if (desktopMinimizedRef.current) {
          restoreAll();
          desktopMinimizedRef.current = false;
        } else {
          minimizeAll();
          desktopMinimizedRef.current = true;
        }
        return;
      }

      // Ctrl+L — open layout/personalization menu
      if (ctrl && key === "l") {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("opentool-toggle-personalization"));
        return;
      }

      // Ctrl+/ or Cmd+/ — keyboard shortcuts overlay
      if (ctrl && key === "/") {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
        return;
      }

      // Ctrl+, — open Settings
      if (ctrl && key === ",") {
        e.preventDefault();
        openWindow("settings");
        return;
      }

      // Ctrl+1 through Ctrl+9 — open pinned app by position
      if (ctrl && !alt && !e.shiftKey && key >= "1" && key <= "9") {
        e.preventDefault();
        const idx = parseInt(key, 10) - 1;
        if (idx < PINNED_APPS.length) {
          openWindow(PINNED_APPS[idx].id);
        }
        return;
      }

      // Ctrl+Arrow — window snapping/tiling
      if (ctrl && !alt && !e.shiftKey) {
        if (key === "arrowleft") {
          e.preventDefault();
          if (activeId) snapWindow(activeId, "left");
          return;
        }
        if (key === "arrowright") {
          e.preventDefault();
          if (activeId) snapWindow(activeId, "right");
          return;
        }
        if (key === "arrowup") {
          e.preventDefault();
          if (activeId) maximizeWindow(activeId);
          return;
        }
        if (key === "arrowdown") {
          e.preventDefault();
          if (activeId) {
            const win = windows.find((w) => w.id === activeId);
            if (win && win.status === "maximized") restoreWindow(activeId);
          }
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    activeId, windows, cycleWindow, closeWindow, maximizeWindow, restoreWindow,
    minimizeWindow, openWindow, minimizeAll, restoreAll, snapWindow,
  ]);

  // Listen for taskbar-driven shortcuts overlay toggle
  useEffect(() => {
    const toggleShortcuts = () => setShortcutsOpen((v) => !v);
    window.addEventListener("opentool-toggle-shortcuts", toggleShortcuts);
    return () => window.removeEventListener("opentool-toggle-shortcuts", toggleShortcuts);
  }, []);

  // Clean up switcher timer on unmount
  useEffect(() => {
    return () => {
      if (switcherTimerRef.current) clearTimeout(switcherTimerRef.current);
    };
  }, []);

  const handleDesktopContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest(".wm-window") ||
        target.closest(".wm-taskbar") ||
        target.closest(".wm-desktop-icon") ||
        target.closest(".wm-ctx")
      ) {
        return;
      }

      const appSubmenu: ContextMenuItem[] = GROUP_ORDER.map((group) => ({
        label: group,
        submenu: ([...APP_REGISTRY]).filter((a) => a.group === group).map((app) => ({
          label: app.label,
          icon: app.icon,
          action: () => {
            openWindow(app.id);
            window.location.hash = app.id;
          },
        })),
      }));

      const items: ContextMenuItem[] = [
        { label: "New Window...", icon: "M12 5v14 M5 12h14", submenu: appSubmenu },
        "separator",
        {
          label: "Arrange Windows",
          icon: "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z",
          submenu: [
            { label: "Cascade", action: () => cascadeWindows() },
            { label: "Tile Horizontally", action: () => tileHorizontally() },
            { label: "Tile Vertically", action: () => tileVertically() },
          ],
        },
        { label: "Show All Windows", icon: "M1 1h10v10H1z M13 1h10v10H13z", action: () => restoreAll() },
        { label: "Minimize All", icon: "M3 19h18", action: () => minimizeAll() },
        "separator",
        {
          label: "Change Background...",
          icon: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z",
          action: () => window.dispatchEvent(new CustomEvent("opentool-toggle-personalization")),
        },
        { label: "Refresh", icon: "M3 12a9 9 0 109-9 M3 3v9h9", action: () => window.location.reload() },
      ];

      desktopCtx.show(e, items);
    },
    [openWindow, cascadeWindows, tileHorizontally, tileVertically, restoreAll, minimizeAll, desktopCtx, APP_REGISTRY],
  );

  return (
    <div className="wm-desktop" onContextMenu={handleDesktopContextMenu}>
      <DesktopBackground />
      <DesktopIconGrid />

      {desktopCtx.render()}

      {/* Alt+Tab window switcher overlay */}
      {switcherVisible && (
        <WindowSwitcher
          windows={windows.filter((w) => w.status !== "minimized")}
          activeId={activeId}
          highlightId={switcherHighlight}
        />
      )}

      {/* Keyboard shortcuts overlay (Ctrl+/) */}
      {shortcutsOpen && (
        <KeyboardShortcutsOverlay onClose={() => setShortcutsOpen(false)} />
      )}

      {/* Spotlight launcher (Ctrl+Space / Ctrl+K) */}
      {spotlightOpen && (
        <SpotlightLauncher
          onNavigate={handleRouteChange}
          onClose={() => setSpotlightOpen(false)}
          minimizeAll={minimizeAll}
          tileHorizontally={tileHorizontally}
          cascadeWindows={cascadeWindows}
          closeAllWindows={closeAllWindows}
          recentWindows={recentWindowsRef.current}
        />
      )}

      <div className="wm-desktop__windows">
        {windows.map((win, idx) => (
          <Window
            key={win.id}
            state={win}
            isActive={win.id === activeId}
            animationIndex={idx}
            onFocus={() => focusWindow(win.id)}
            onClose={() => closeWindow(win.id)}
            onMinimize={() => minimizeWindow(win.id)}
            onMaximize={() => maximizeWindow(win.id)}
            onRestore={() => restoreWindow(win.id)}
            onMove={(pos) => moveWindow(win.id, pos)}
            onResize={(size) => resizeWindow(win.id, size)}
            onSnap={(zone) => snapWindow(win.id, zone)}
            onUnsnap={() => unsnapWindow(win.id)}
            onDragCursorMove={handleDragCursorMove(win.id)}
          >
            {children(win.id, undefined)}
          </Window>
        ))}

        {/* Snap zone preview overlay */}
        {activeSnapZone && <SnapPreview zone={activeSnapZone} />}
      </div>

      <ToastNotifications />

      <Taskbar
        provider={provider}
        onOpenWindow={handleRouteChange}
        activeWindowId={activeId}
      />
    </div>
  );
}
